import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, track, fileUrl, GALLERY_LABELS, GALLERY_ORDER, galleryCategory, isVideo } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

const EMPTY_TEXT = {
  storiche: "Le fotografie dell'archivio di famiglia saranno presto disponibili.",
  ambienti: "Le fotografie della sala, della terrazza e degli spazi allestiti saranno presto disponibili.",
  cucina: "Foto e video delle preparazioni, della pasta fresca e della griglia saranno presto disponibili.",
};

// Colonne masonry (CSS columns) per storiche/cucina; griglia a 2 colonne per gli ambienti
const LAYOUT = {
  storiche: "columns-1 sm:columns-2 lg:columns-3 gap-5 sm:gap-6 [&>*]:mb-5 sm:[&>*]:mb-6 [&>*]:break-inside-avoid",
  ambienti: "grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6",
  cucina: "columns-1 sm:columns-2 lg:columns-3 gap-5 sm:gap-6 [&>*]:mb-5 sm:[&>*]:mb-6 [&>*]:break-inside-avoid",
};

const ratio = (p) => (p.width && p.height ? `${p.width} / ${p.height}` : undefined);

function Thumb({ item }) {
  const alt = item.image_alt || item.title || "Foto Masetti";
  const cls = "w-full h-full object-cover";
  if (isVideo(item.image)) {
    return <video src={fileUrl(item.image)} className={cls} muted playsInline preload="metadata" aria-label={alt} />;
  }
  return <img src={fileUrl(item.image)} alt={alt} loading="lazy" decoding="async" className={cls} />;
}

function Card({ item, onOpen, testid }) {
  const r = ratio(item);
  return (
    <figure data-testid={testid}>
      <button type="button" onClick={onOpen} aria-label={`Ingrandisci: ${item.title || item.image_alt || "foto"}`}
        className="relative block w-full rounded-lg overflow-hidden bg-oak-dark/5 group cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-bordeaux/60"
        style={{ aspectRatio: r || (isVideo(item.image) ? "16 / 9" : "4 / 3") }}>
        <div className="absolute inset-0"><Thumb item={item} /></div>
        {isVideo(item.image) && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="w-14 h-14 rounded-full bg-cream/90 text-bordeaux flex items-center justify-center shadow-md group-hover:scale-105 transition-transform"><Play className="w-6 h-6 ml-0.5" fill="currentColor" /></span>
          </span>
        )}
      </button>
      {(item.title || item.caption) && (
        <figcaption className="mt-2 text-sm sm:text-[15px] text-oak leading-snug" data-testid={`${testid}-caption`}>
          {item.title && <span className="block">{item.title}</span>}
          {item.caption && <span className="block text-oak/70">{item.caption}</span>}
        </figcaption>
      )}
    </figure>
  );
}

export default function Galleria() {
  const { settings } = useSettings();
  const { hash } = useLocation();
  const [items, setItems] = useState([]);
  const [lb, setLb] = useState(null); // { cat, idx }
  const touchX = useRef(null);

  useEffect(() => {
    track("page_view", "galleria");
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const scrollTo = (c, smooth = true) => {
    const el = document.getElementById(c);
    if (!el) return;
    const offset = window.innerWidth < 640 ? 88 : 104;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: smooth ? "smooth" : "auto" });
  };

  useEffect(() => {
    const c = hash.replace("#", "");
    if (!items.length || !GALLERY_ORDER.includes(c)) return;
    const start = Date.now();
    const id = setInterval(() => {
      scrollTo(c, false);
      if (Date.now() - start > 2000) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [hash, items.length]);

  const byCat = (c) => items.filter((i) => galleryCategory(i.category) === c);
  const photos = lb ? byCat(lb.cat) : [];
  const current = lb ? photos[lb.idx] : null;

  const go = useCallback((d) => setLb((s) => {
    if (!s) return s;
    const n = byCat(s.cat).length;
    return { ...s, idx: (s.idx + d + n) % n };
  }), [items]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lb) return;
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lb, go]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div className="overflow-x-hidden">
      <SEO title="Galleria" description="Le fotografie del Ristorante Masetti: le foto storiche, gli ambienti e la nostra cucina." />
      <PageHero eyebrow="Le nostre fotografie" title="Galleria" cover={settings.galleria_cover} alt={settings.galleria_cover_alt} />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <nav className="flex flex-wrap gap-2 sm:gap-3 mb-10 sm:mb-14" aria-label="Sezioni della galleria" data-testid="gallery-tabs">
          {GALLERY_ORDER.map((c) => (
            <a key={c} href={`#${c}`} onClick={(e) => { e.preventDefault(); window.history.replaceState(null, "", `#${c}`); scrollTo(c); }} data-testid={`gallery-tab-${c}`}
              className="rounded-full px-5 h-10 inline-flex items-center text-sm font-medium border border-bordeaux/30 text-bordeaux bg-transparent hover:bg-bordeaux hover:text-cream transition-colors whitespace-nowrap">
              {GALLERY_LABELS[c]}
            </a>
          ))}
        </nav>

        <div className="space-y-10 md:space-y-16 pb-[40vh]">
          {GALLERY_ORDER.map((c) => {
            const list = byCat(c);
            return (
              <section key={c} id={c} className="scroll-mt-28 sm:scroll-mt-32" data-testid={`gallery-section-${c}`}>
                <h2 className="font-serif text-2xl sm:text-3xl text-bordeaux mb-1">{GALLERY_LABELS[c]}</h2>
                <div className="section-divider w-full mb-6 sm:mb-8" />
                {list.length > 0 ? (
                  <div className={LAYOUT[c]}>
                    {list.map((p, i) => (
                      <Card key={p.id} item={p} onOpen={() => setLb({ cat: c, idx: i })} testid={`gallery-photo-${p.id}`} />
                    ))}
                  </div>
                ) : (
                  <p className="text-oak/60 py-6" data-testid={`gallery-empty-${c}`}>{EMPTY_TEXT[c]}</p>
                )}
              </section>
            );
          })}
        </div>
      </div>

      <Dialog open={!!current} onOpenChange={(v) => !v && setLb(null)}>
        <DialogContent className="max-w-[96vw] sm:max-w-5xl p-2 sm:p-3 bg-oak-dark border-brass/30 overflow-hidden [&>button]:bg-cream/90 [&>button]:text-bordeaux [&>button]:rounded-full [&>button]:p-1.5 [&>button]:opacity-100 [&>button]:right-3 [&>button]:top-3" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{current?.title || "Fotografia Ristorante Masetti"}</DialogTitle>
          {current && (
            <figure onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="relative select-none">
              {isVideo(current.image)
                ? <video key={current.id} src={fileUrl(current.image)} controls playsInline preload="metadata" className="w-full max-h-[80vh] object-contain rounded-lg bg-black" aria-label={current.image_alt || current.title || "Video Masetti"} />
                : <img key={current.id} src={fileUrl(current.image)} alt={current.image_alt || current.title || "Foto Masetti"} className="w-full max-h-[80vh] object-contain rounded-lg" />}
              {photos.length > 1 && (
                <>
                  <button type="button" onClick={() => go(-1)} aria-label="Foto precedente" data-testid="gallery-prev"
                    className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-oak-dark/60 hover:bg-oak-dark/85 text-cream flex items-center justify-center backdrop-blur-sm transition-colors">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button type="button" onClick={() => go(1)} aria-label="Foto successiva" data-testid="gallery-next"
                    className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-oak-dark/60 hover:bg-oak-dark/85 text-cream flex items-center justify-center backdrop-blur-sm transition-colors">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <figcaption className="text-center mt-2 px-2">
                {current.title && <h3 className="font-serif text-lg text-cream">{current.title}</h3>}
                {current.caption && <p className="text-cream/80 text-sm">{current.caption}</p>}
                {photos.length > 1 && <p className="text-cream/50 text-xs mt-1" data-testid="gallery-counter">{lb.idx + 1} / {photos.length}</p>}
              </figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
